/**
 * One source of truth for React Query keys. The HubProvider invalidates by
 * key; widgets fetch by key. Keep the keys here so a refactor doesn't drift.
 */
export const qk = {
    /** The games board. By convention the frontend uses ONLY the no-arg key —
     *  one cache entry that every consumer shares via useGames(), which is
     *  what lets the `gamesChanged` push handler invalidate a single key. If
     *  you add a phase-keyed query, the push handler will not cover it. */
    games: (phase?: string) => ['games', phase ?? 'all'] as const,
    /** A finished game's recorded book. Deliberately keyed under `['games',
     *  <id>, …]` rather than the board key: `gamesChanged` invalidates
     *  `['games','all']` exactly, so a score push can't force a refetch of a
     *  few hundred snapshot points per open review. */
    gameMarketHistory: (id: string) => ['games', id, 'market-history'] as const,
    instances: () => ['instances'] as const,
    activeInstances: () => ['instances', 'active'] as const,
    instance: (id: string) => ['instance', id] as const,
    instanceMarketHistory: (id: string) => ['instance', id, 'market-history'] as const,
    /** Prefix over every single-instance entry (detail + market history).
     *  `instanceStateChanged` invalidates it so a fill's trade marks land on
     *  the next render instead of waiting out the seed's refetch interval —
     *  fills reach the client through state changes, never the price hub. */
    instanceScope: () => ['instance'] as const,
    /** Tonight's slate for one algorithm. Under the `['instances']` prefix so
     *  every instance-state invalidation also refreshes who is armed where. */
    slate: (algorithmName: string) => ['instances', 'slate', algorithmName] as const,
    algorithms: () => ['algorithms'] as const,
    /** What a configuration would stake — keyed by the exact config JSON, so
     *  each edit is its own server answer. */
    stakeQuote: (algorithmName: string, configJson: string) =>
        ['algorithms', algorithmName, 'stake', configJson] as const,
    me: () => ['user', 'me'] as const,
    preferences: () => ['user', 'preferences'] as const,
    credentialsStatus: () => ['user', 'credentials-status'] as const,
    kalshiBalance: () => ['user', 'kalshi-balance'] as const,
    dashboardLayout: () => ['dashboard', 'layout'] as const,
    dashboardSummary: () => ['dashboard', 'summary'] as const,
    /** Keyed by the whole filter object so each distinct combination is its
     *  own cache entry; invalidating the `['history']` prefix refreshes every
     *  filtered view at once. */
    historySummary: (filters?: Record<string, unknown>) =>
        ['history', 'summary', filters ?? {}] as const,
    historyTrades: (filters?: Record<string, unknown>) =>
        ['history', 'trades', filters ?? {}] as const,
    historyTrade: (instanceId: string) => ['history', 'trade', instanceId] as const,
    adminUsers: () => ['admin', 'users'] as const,
    adminCommissions: (status?: string) => ['admin', 'commissions', status ?? 'all'] as const,
    adminCommissionReport: (id: string) => ['admin', 'commissions', 'report', id] as const,
    /** Keyed by the whole filter set so each distinct combination is its own
     *  cache entry. The `adminAudit` push handler invalidates the
     *  `['admin','audit']` prefix, which still matches every filtered entry and
     *  the actions list below (React Query does prefix matching). */
    adminAudit: (filters?: Record<string, unknown>) => ['admin', 'audit', filters ?? {}] as const,
    adminAuditActions: () => ['admin', 'audit', 'actions'] as const,
    adminSettings: () => ['admin', 'settings'] as const,
    /** Prefix for every admin-recordings cache entry — invalidating it after a
     *  mutation refetches both the paged catalog and the requests list below. */
    adminRecordings: () => ['admin', 'recordings'] as const,
    adminRecordingsPage: (page: number) => ['admin', 'recordings', 'list', page] as const,
    adminRecordingRequests: () => ['admin', 'recordings', 'requests'] as const,
    /** Prefix for every engine-analyses cache entry; the
     *  `engineAnalysisPublished` push invalidates the prefix so the widget
     *  list and any open detail refresh together (prefix matching). */
    engineAnalyses: () => ['engine', 'analyses'] as const,
    engineAnalysis: (id: string) => ['engine', 'analyses', 'detail', id] as const,
    adminEngineStatus: () => ['admin', 'engine', 'status'] as const,
    adminEngineObservations: (league: string) =>
        ['admin', 'engine', 'observations', league] as const,
    adminEngineRules: () => ['admin', 'engine', 'rules'] as const,
    /** Prefix for every engine-metrics cache entry — the P4 Engine section
     *  invalidates it on `engineAnalysisPublished` (prefix matching), so any
     *  new metrics query must key under it. */
    adminEngineMetrics: () => ['admin', 'engine', 'metrics'] as const,
    /** Keyed by the whole filter object (league/from/to) so each distinct
     *  window is its own cache entry — the History filter-key discipline. */
    adminEngineMetricsOverview: (filters?: Record<string, unknown>) =>
        ['admin', 'engine', 'metrics', 'overview', filters ?? {}] as const,
    adminEngineMetricsBlockers: (filters?: Record<string, unknown>) =>
        ['admin', 'engine', 'metrics', 'blockers', filters ?? {}] as const,
    adminEngineMetricsLedger: (filters?: Record<string, unknown>) =>
        ['admin', 'engine', 'metrics', 'ledger', filters ?? {}] as const,
    adminEngineMetricsExecution: (filters?: Record<string, unknown>) =>
        ['admin', 'engine', 'metrics', 'execution', filters ?? {}] as const,
    /** The `metric` choice (target | fill) keys separately — the two tabs are
     *  distinct server queries sharing the filter window. */
    adminEngineMetricsCalibration: (metric: string, filters?: Record<string, unknown>) =>
        ['admin', 'engine', 'metrics', 'calibration', metric, filters ?? {}] as const,
    /** Deliberately outside the metrics prefix: `engineAnalysisPublished`
     *  invalidations must not refetch the viewer's board layout. */
    adminEngineLayout: () => ['admin', 'engine', 'layout'] as const,
    replayGames: () => ['replay', 'games'] as const,
    replaySessions: () => ['replay', 'sessions'] as const,
    replaySession: (id: string) => ['replay', 'session', id] as const,
    replaySessionDetail: (id: string) => ['replay', 'session', id, 'detail'] as const,
}
